import { useEffect, useMemo, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import type { Map as MapboxMap } from 'mapbox-gl';
import type { EventWithCircuit } from '../../api/eventsService';
import {
  getEventKey,
  resolveMapViewForEvent,
  saveMapViewForEvent,
} from './nowcastingStorage';
import { resolveMapStyle } from './nowcastingMapIds';

// ============================================================================
// Types
// ============================================================================

export interface MapStyleConfig {
  name: string;
  url: string;
}

// Use Record type for compatibility with consumers (usePiPMap, MapSettingsPanel)
// while maintaining known keys for type-safe access
export type MapStyles = Record<string, MapStyleConfig> & {
  satellite: MapStyleConfig;
  terrain: MapStyleConfig;
};

export interface UseMapInitializationParams {
  selectedEvent: EventWithCircuit | null;
  mapStyle: string | null;
  theme: string;
  mapContainerRef: React.RefObject<HTMLDivElement | null>;
  mapRef: React.RefObject<MapboxMap | null>;
  setStyleVersion: React.Dispatch<React.SetStateAction<number>>;
  /** Skip localStorage view save/load (useful for dashboard widgets that persist view in config). */
  disableViewPersistence?: boolean;
  /** Override initial center instead of loading from storage or using circuit center. */
  initialCenter?: [number, number];
  /** Override initial zoom instead of loading from storage or using default 13. */
  initialZoom?: number;
  /** Expand the Mapbox attribution info label on load (default true). */
  expandAttributionOnLoad?: boolean;
  /** Show the bottom-left zoom level label (default true). */
  showZoomIndicator?: boolean;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to handle Mapbox map initialization and cleanup.
 *
 * The map is created once per style/theme change. Switching events reuses the
 * same map instance and jumps to the saved view instead of recreating it.
 */
export function useMapInitialization({
  selectedEvent,
  mapStyle,
  theme,
  mapContainerRef,
  mapRef,
  setStyleVersion,
  disableViewPersistence = false,
  initialCenter,
  initialZoom,
  expandAttributionOnLoad = true,
  showZoomIndicator = true,
}: UseMapInitializationParams) {
  const mapStyles = useMemo<MapStyles>(() => ({
    satellite: {
      name: 'Satellite',
      url: 'mapbox://styles/mapbox/satellite-streets-v12',
    },
    terrain: {
      name: 'Terrain',
      url: 'mapbox://styles/mapbox/outdoors-v12',
    },
  }), []);

  const selectedEventRef = useRef(selectedEvent);
  selectedEventRef.current = selectedEvent;

  const previousEventKeyRef = useRef<string | null>(null);
  const eventReady = selectedEvent != null;

  // Create or recreate the map when style/theme changes (not on event switch).
  useEffect(() => {
    if (!mapContainerRef.current || !selectedEvent) return;

    const mapContainer = mapContainerRef.current;
    const { center: resolvedCenter, zoom: resolvedZoom } = resolveMapViewForEvent(
      selectedEvent,
      { initialCenter, initialZoom, disableViewPersistence },
    );

    const styleUrl = resolveMapStyle(mapStyle, mapStyles, theme);

    const map = new mapboxgl.Map({
      container: mapContainer,
      style: styleUrl,
      center: resolvedCenter,
      zoom: resolvedZoom,
      minZoom: 6,
      projection: 'mercator',
      preserveDrawingBuffer: true,
      ...(expandAttributionOnLoad ? {} : { attributionControl: false }),
      dragPan: true,
      scrollZoom: true,
      doubleClickZoom: true,
      boxZoom: true,
      keyboard: true,
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    if (!expandAttributionOnLoad) {
      map.addControl(new mapboxgl.AttributionControl({ compact: true }));
    }

    let updateZoomIndicator: (() => void) | null = null;
    if (showZoomIndicator) {
      const zoomIndicator = document.createElement('div');
      zoomIndicator.setAttribute('data-zoom-indicator', 'true');
      zoomIndicator.className = 'mapboxgl-ctrl';
      zoomIndicator.style.cssText = `
        position: absolute;
        bottom: 20px;
        left: 20px;
        background: ${theme === 'dark' ? 'rgba(0, 0, 0, 0.75)' : 'rgba(255, 255, 255, 0.9)'};
        color: ${theme === 'dark' ? '#fff' : '#000'};
        padding: 6px 12px;
        border-radius: 4px;
        font-size: 12px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
        z-index: 1;
        pointer-events: none;
        user-select: none;
      `;
      zoomIndicator.textContent = `Zoom: ${map.getZoom().toFixed(1)}`;
      mapContainer.appendChild(zoomIndicator);

      updateZoomIndicator = () => {
        zoomIndicator.textContent = `Zoom: ${map.getZoom().toFixed(1)}`;
      };
      map.on('zoom', updateZoomIndicator);
      map.on('zoomend', updateZoomIndicator);
    }

    const onMoveEnd = disableViewPersistence ? null : () => {
      const event = selectedEventRef.current;
      if (!event) return;
      saveMapViewForEvent(event, map.getCenter(), map.getZoom());
    };
    if (onMoveEnd) map.on('moveend', onMoveEnd);

    map.on('load', () => {
      map.resize();
      setStyleVersion(v => v + 1);

      if (expandAttributionOnLoad) {
        setTimeout(() => {
          const attributionButton = mapContainer.querySelector('.mapboxgl-ctrl-attrib-button') as HTMLElement;
          if (attributionButton) {
            attributionButton.click();
          }
        }, 100);
      }
    });

    mapRef.current = map;
    previousEventKeyRef.current = getEventKey(selectedEvent);

    return () => {
      if (onMoveEnd) map.off('moveend', onMoveEnd);
      if (updateZoomIndicator) {
        map.off('zoom', updateZoomIndicator);
        map.off('zoomend', updateZoomIndicator);
        mapContainer.querySelector('[data-zoom-indicator="true"]')?.remove();
      }
      map.remove();
      mapRef.current = null;
      previousEventKeyRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventReady, mapStyle, mapStyles, theme, mapContainerRef, mapRef, setStyleVersion, expandAttributionOnLoad, showZoomIndicator]);

  // Reuse the map when switching events — jump to each event's saved view.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedEvent) return;

    const eventKey = getEventKey(selectedEvent);
    if (previousEventKeyRef.current === eventKey) return;

    previousEventKeyRef.current = eventKey;
    const { center, zoom } = resolveMapViewForEvent(selectedEvent, {
      initialCenter,
      initialZoom,
      disableViewPersistence,
    });

    map.jumpTo({ center, zoom });
  }, [selectedEvent, mapRef, initialCenter, initialZoom, disableViewPersistence]);

  return { mapStyles };
}
